import { useState, useEffect } from 'react';
import SeoHead from '../../components/base/SeoHead';

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首頁", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "關於我們", "item": `${siteUrl}/about` }
        ]
      },
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "MOFU 淨寵居",
        "description": "新北市新莊區專業寵物空氣清淨機與寵物友善香氛產品品牌",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
        },
        "image": "https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png",
        "telephone": "+886-2-2202-3192",
        "email": "hello@mofu.com.tw",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "瓊林南路187巷29號",
          "addressLocality": "新北市",
          "addressRegion": "新莊區",
          "postalCode": "242",
          "addressCountry": "TW"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "25.038",
          "longitude": "121.432"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "10:00",
            "closes": "20:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday", "Sunday"],
            "opens": "11:00",
            "closes": "19:00"
          }
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+886-2-2202-3192",
          "contactType": "customer service",
          "email": "hello@mofu.com.tw",
          "availableLanguage": ["zh-TW"]
        },
        "sameAs": [
          "https://facebook.com/mofu",
          "https://instagram.com/tulip32240",
          "https://line.me/ti/p/@mofu"
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "MOFU 淨寵居在哪裡？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "MOFU 淨寵居位於台灣新北市新莊區瓊林南路187巷29號。週一至週五 10:00-20:00，週六至週日 11:00-19:00。"
            }
          },
          {
            "@type": "Question",
            "name": "香氛產品對寵物安全嗎？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "MOFU 淨寵居所有香氛產品均經過嚴格的寵物安全測試，使用天然成分，確保對貓狗等寵物安全無害。"
            }
          },
          {
            "@type": "Question",
            "name": "如何預約香氛實驗室課程？",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "您可以透過官網的香氛實驗室頁面線上預約，或致電 02-2202-3192 洽詢課程資訊。"
            }
          }
        ]
      }
    ]
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      setSubmitMessage('請填寫所有必填欄位');
      return;
    }

    if (formData.phone.length !== 10) {
      setSubmitMessage('請輸入正確的手機號碼（10位數字）');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('訊息內容不能超過500字');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('subject', formData.subject);
      submitData.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4mm1ev7s74mkk8pe0ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitMessage('訊息已送出！我們會盡快與您聯繫');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitMessage('送出失敗，請稍後再試');
      }
    } catch (error) {
      setSubmitMessage('送出失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const teamMembers = [
    {
      name: 'Tulips/吳鈺淳',
      position: '創辦人 & 執行長',
      image: 'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/b197c38dcbadbeb4e2bef2e37f5103c8.jpeg',
      bio: ` Mofu 寵物香氛｜創辦人
🏫 韓國ISIPCA調香證書
英國IPHM寵物芳療與溝通療癒證書
HWAC專業寵物管理師
HWCA HTP心理圖像調香講師
🌿 讓毛孩的日常更舒服、更安心
📍創業筆記｜香氛分享｜毛孩生活美學
🐈 11隙貓的香氛翻譯官`
    },
    {
      name: 'Lee/李卓翰',
      position: '香氛總監',
      image: 'https://public.readdy.ai/ai/img_res/b9548bd4ca813895274a41247937bc93.jpg',
      bio: '在國際 香氛悠久的歷史國家中，長年學習與積累經驗，有著資深香氛經驗'
    }
  ];

  const values = [
    {
      icon: 'ri-heart-line',
      title: '寵物優先',
      description: '所有產品都以寵物安全為首要考量，使用天然成分，經過嚴格測試'
    },
    {
      icon: 'ri-star-line',
      title: '品質保證',
      description: '嚴格的品質控管，確保每一件產品都達到最高標準'
    },
    {
      icon: 'ri-team-line',
      title: '專業服務',
      description: '專業團隊提供完整的售前售後服務，讓您購物無憂'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      <SeoHead
        title="關於我們 - 新北市新莊區品牌故事與聯絡方式 | MOFU 淨寵居"
        description="MOFU 淨寵居成立於2024年，位於新北市新莊區，致力於研發對寵物安全、對環境友善的空氣清淨與香氛產品。提供線上購物與香氛實驗室課程。聯絡電話：02-2202-3192。寵物空氣清淨機、寵物友善香氛、香氛實驗室是我們的核心服務。"
        keywords="關於MOFU,品牌故事,聯絡方式,新北市新莊區,寵物空氣清淨機"
        ogImage="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-coral-100 text-coral-500 rounded-full text-sm font-medium mb-6 whitespace-nowrap">
                關於我們
              </div>
              <h1 className="text-5xl font-bold text-gray-800 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                我們的故事
              </h1>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                MOFU 淨寵居 品牌成立於2024年 源自於創辦人對寵物的深厚情感與對生活品質的追求。我們發現市面上缺乏真正為寵物家庭設計的空氣清淨與香氛產品，於是決定創立這個品牌。
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                來自松林夏冷氣的專業技術與品質保證。淨寵居是專為寵物家庭而生的新創品牌，背後承載著 <strong>松林夏</strong> 超過30年的空氣淨化專業經驗。身為松林夏的相關企業。
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                <strong>寵物家庭的日常煩惱，我們懂。</strong>
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                你是否曾因家中揮之不去的寵物氣味而感到困擾？ 儘管每天打掃，那股「寵物味」似乎總是如影隨形。一般的空氣清淨機只能過濾PM2.5和部分懸浮物，卻無法有效分解異味分子，更別提許多化學香氛對寵物來說是刺激且有害的。
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                <strong>淨寵居：專為寵物打造的清新方案</strong>
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                「淨寵居」空氣香氛清淨機，是我們專為寵物家庭研發的革命性產品。它不僅僅是一台清淨機，更是一套完整的寵物居家空氣管理方案。
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                身為多貓家庭的一份子，我們也曾面臨清掃時貓毛滿天飛舞，以及空氣中難以散去的異味，這些都悄悄影響著居家品質。儘管市面上的空氣清淨機選擇眾多，我們卻發現，它們大多只能處理懸浮微粒，對於寵物帶來的深層異味，卻總是力有未逮。作為一位同時熱愛香氛的創業者，我深知氣味對居家氛圍的重要性。為何不能有一款空氣清淨機，不僅能有效吸附毛髮、淨化空氣，還能真正解決寵物異味問題，並讓家充滿令人愉悅的香氣呢？
這份對毛孩的深切關愛，以及對清新空氣的渴望，激發了我們創立 Mofu淨寵居 的初心。
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                除了產品研發，我們也開設香氛實驗室，希望透過手作課程，讓更多人了解香氛的魅力，並學習如何在日常生活中安全地使用香氛產品。
              </p>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <div className="w-full h-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=peaceful%20aromatherapy%20workspace%20interior%20featuring%20adorable%20sleeping%20cats%20on%20cushions%20beside%20elegant%20glass%20essential%20oil%20bottles%20and%20modern%20reed%20diffusers%20arranged%20on%20natural%20wooden%20table%20surface%2C%20bright%20airy%20room%20with%20large%20windows%20providing%20soft%20daylight%2C%20lush%20green%20potted%20plants%20creating%20fresh%20atmosphere%2C%20minimalist%20scandinavian%20design%20aesthetic%2C%20professional%20fragrance%20laboratory%20setting%20that%20welcomes%20pets%2C%20serene%20and%20inviting%20ambiance%20perfect%20for%20scent%20creation%2C%20contemporary%20lifestyle%20interior%20photography%20showcasing%20harmony%20between%20pets%20and%20aromatherapy%20crafting%20space&width=800&height=600&seq=about-lab-cats-v3&orientation=landscape"
                    alt="MOFU Store"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              品牌理念
            </h2>
            <p className="text-gray-600 text-lg">我們堅持的核心價值</p>
          </div>

          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-coral-50 to-orange-50 rounded-3xl p-8 text-center hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl mx-auto mb-6 shadow-sm">
                    <i className={`${value.icon} text-3xl text-coral-500`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              認識我們的團隊
            </h2>
            <p className="text-gray-600 text-lg">專業、熱情、用心的團隊成員</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative aspect-square overflow-hidden">
                  <div className="w-full h-full">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-coral-500 font-medium mb-3">{member.position}</p>
                  <p className="text-sm text-gray-600">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Contact Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              聯絡我們
            </h2>
            <p className="text-gray-600 text-lg">歡迎來店參觀或線上諮詢</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Map */}
            <div className="lg:col-span-3">
              <div className="rounded-3xl overflow-hidden shadow-lg h-96 lg:h-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.3!2d121.43!3d25.038!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442a8e0e0e0e0e0%3A0x0!2zMjXCsDAyJzE2LjgiTiAxMjHCsDI1JzU1LjIiRQ!5e0!3m2!1szh-TW!2stw!4v1234567890123!5m2!1szh-TW!2stw"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MOFU Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-8 shadow-lg h-full">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">公司與香氛實驗室資訊</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-coral-100 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-coral-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">地址</p>
                      <p className="text-gray-600">台灣新北市新莊區瓊林南路187巷29號</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-coral-100 rounded-xl flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-coral-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">電話</p>
                      <p className="text-gray-600">02-2202-3192</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-coral-100 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-coral-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">電子郵件</p>
                      <p className="text-gray-600">hello@mofu.com.tw</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-coral-100 rounded-xl flex-shrink-0">
                      <i className="ri-time-line text-2xl text-coral-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 mb-1">營業時間</p>
                      <p className="text-gray-600">週一至週五 10:00-20:00</p>
                      <p className="text-gray-600">週六至週日 11:00-19:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              線上諮詢
            </h2>
            <p className="text-gray-600 text-lg">有任何問題嗎？歡迎與我們聯繫</p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    姓名 <span className="text-coral-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    maxLength={50}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm"
                    placeholder="請輸入您的姓名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    電子郵件 <span className="text-coral-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    maxLength={100}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    手機號碼 <span className="text-coral-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    required
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm"
                    placeholder="請輸入10位數字"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    主旨 <span className="text-coral-500">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm cursor-pointer"
                  >
                    <option value="">請選擇主旨</option>
                    <option value="產品諮詢">產品諮詢</option>
                    <option value="課程預約">課程預約</option>
                    <option value="售後服務">售後服務</option>
                    <option value="合作提案">合作提案</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  訊息內容 <span className="text-coral-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm resize-none"
                  placeholder="請輸入您的訊息（限500字）"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '送出中...' : '送出訊息'}
              </button>

              {submitMessage && (
                <p className={`mt-4 text-center text-sm ${submitMessage.includes('成功') ? 'text-green-600' : 'text-red-600'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}